import { useSnackbar } from "@/composables/useSnackbar";
import axios from "axios";
import { consumirMotivoDeExclusao } from "@/services/confirmService";
import { emitPatientDataChanged } from "./patientDataBus";

// SALVIA-744 — autenticação por cookie de sessão httpOnly (modo SPA do
// Sanctum). Não existe mais token em localStorage: o navegador manda o cookie
// sozinho, e nenhum script da página consegue lê-lo.
//
//   withCredentials  envia o cookie de sessão (necessário em dev, onde front e
//                    API estão em portas diferentes; em test/stage é a mesma
//                    origem e não faz diferença).
//   withXSRFToken    copia o cookie XSRF-TOKEN para o header X-XSRF-TOKEN em
//                    toda requisição — o axios 1.x só faz isso sozinho na
//                    MESMA origem. É a proteção CSRF que o cookie exige.
const http = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  withCredentials: true,
  withXSRFToken: true,
});

const CSRF_COOKIE_URL = "/api/sanctum/csrf-cookie";

/** O cookie XSRF-TOKEN já existe? (ele NÃO é httpOnly: foi feito para ser lido.) */
function temCookieCsrf() {
  return document.cookie.split(";").some((c) => c.trim().startsWith("XSRF-TOKEN="));
}

let csrfEmVoo = null;

/**
 * Garante o cookie de CSRF antes de uma mutação. Uma chamada só, mesmo com
 * várias mutações disparadas juntas.
 */
export function garantirCookieCsrf({ forcar = false } = {}) {
  if (!forcar && temCookieCsrf()) return Promise.resolve();
  if (!csrfEmVoo) {
    csrfEmVoo = http.get(CSRF_COOKIE_URL).finally(() => {
      csrfEmVoo = null;
    });
  }
  return csrfEmVoo;
}

const MUTATING_METHODS = ["post", "put", "patch", "delete"];

// ─────────────────────────────────────────────────────────────────────────
// Fase 3 — Blindagem da camada HTTP (defense-in-depth contra 429)
//
// O dashboard do paciente dispara muitas leituras em rajada (um card por
// recurso, e "atualizou um → atualiza todos"). Sem mudar NENHUM comportamento,
// aqui nós:
//   1. Deduplicamos GETs idênticos em voo (mesma URL+params → mesma promise);
//   2. Limitamos a concorrência de GETs (a rajada vira ondas suaves);
//   3. Reexecutamos automaticamente em 429 com backoff (respeitando Retry-After).
// Nada disso altera os dados retornados nem a semântica de sincronização.
// ─────────────────────────────────────────────────────────────────────────

const MAX_CONCURRENT_GETS = 6;
const MAX_RETRIES_429 = 3;

let activeGets = 0;
const getQueue = [];
const inflightGets = new Map();

function pumpGetQueue() {
  if (activeGets >= MAX_CONCURRENT_GETS || getQueue.length === 0) return;
  activeGets += 1;
  const { run, resolve, reject } = getQueue.shift();
  run()
    .then(resolve, reject)
    .finally(() => {
      activeGets -= 1;
      pumpGetQueue();
    });
}

/** Enfileira uma execução respeitando o teto de concorrência. */
function scheduleGet(run) {
  return new Promise((resolve, reject) => {
    getQueue.push({ run, resolve, reject });
    pumpGetQueue();
  });
}

function getKey(url, config) {
  let params = "";
  try {
    params = JSON.stringify(config?.params || {});
  } catch {
    params = "";
  }
  return `${url}?${params}`;
}

// Envolve http.get com dedup + limitador. Chamadas idênticas concorrentes
// compartilham a MESMA promise; o excesso aguarda uma vaga na fila.
const rawGet = http.get.bind(http);
http.get = function dedupedLimitedGet(url, config) {
  const key = getKey(url, config);
  const existing = inflightGets.get(key);
  if (existing) return existing;

  const promise = scheduleGet(() => rawGet(url, config)).finally(() => {
    inflightGets.delete(key);
  });
  inflightGets.set(key, promise);
  return promise;
};

// CSRF antes de mutação + Socket-ID para broadcasting (toOthers)
/**
 * SALVIA-951 — identificador de correlação gerado AQUI, antes de enviar.
 *
 * Antes o front só lia o id que o servidor devolvia. Quando a resposta não
 * vinha do Laravel — 504 do proxy numa transcrição longa, bloqueio do WAF,
 * queda de rede —, o aviso saía sem referência e não havia como rastrear.
 * Gerando o id no cliente, o aviso SEMPRE tem o que mostrar, e é o mesmo id
 * que o Laravel usa no log (AssignRequestId aceita o X-Request-Id recebido,
 * no formato [A-Za-z0-9._-]{8,64}) e repassa aos serviços (Rastro).
 */
function novoRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  const r = () => Math.random().toString(16).slice(2, 10);
  return `${r()}-${r()}-${r()}`;
}

http.interceptors.request.use(async (config) => {
  config.headers = config.headers || {};
  if (!config.headers["X-Request-Id"]) {
    config.headers["X-Request-Id"] = novoRequestId();
  }

  // SALVIA-744: POST/PUT/PATCH/DELETE vindos do front exigem o token CSRF.
  // Sem o cookie ainda (primeira visita, cookie expirado), busca antes.
  if (
    MUTATING_METHODS.includes(String(config.method).toLowerCase()) &&
    config.url !== CSRF_COOKIE_URL
  ) {
    await garantirCookieCsrf();
  }

  // Necessário para que broadcast()->toOthers() no Laravel exclua o remetente
  const socketId = window.Echo?.socketId?.();
  if (socketId) config.headers["X-Socket-ID"] = socketId;

  // salvia-776: o motivo da exclusão viaja SOZINHO, em cabeçalho.
  //
  // POR QUE AQUI: são 36 chamadas `http.delete(url)` espalhadas pelo sistema.
  // Levar o motivo no corpo obrigaria a reescrever todas para
  // `http.delete(url, { data })` -- o axios não manda corpo em DELETE. No
  // interceptor, nenhuma delas muda, e a 37ª que alguém escrever amanhã já
  // nasce coberta.
  //
  // CONSUMO ÚNICO (ver confirmService): quem lê, zera. O motivo pertence
  // àquela exclusão; sobrando, a próxima requisição carregaria a
  // justificativa da anterior -- e a auditoria registraria a razão errada,
  // com toda a aparência de estar certa.
  if (String(config.method).toLowerCase() === "delete") {
    const motivo = consumirMotivoDeExclusao();

    if (motivo) config.headers["X-Motivo-Exclusao"] = encodeURIComponent(motivo);
  }

  return config;
});

// Endpoints em que 401 significa "credencial errada", não "sessão expirada".
// Redirecionar a partir deles apagaria a mensagem de erro que o usuário
// precisa ler ("senha inválida") e criaria laço na própria tela de login.
const ROTAS_DE_CREDENCIAL = [
  "/api/login",
  "/api/logout",
  "/api/forgot-password",
  "/api/reset-password",
  "/api/auth/google/login/exchange",
];

/**
 * Trata 401 limpando a sessão e MANDANDO O USUÁRIO PARA O LOGIN.
 *
 * Antes o interceptor só limpava o localStorage e deixava a pessoa na tela.
 * Com token de 30 dias isso quase nunca acontecia; com validade curta passa a
 * ser rotina, e sem redirecionar o usuário veria ações falhando em silêncio,
 * sem entender por quê, até navegar para outra página por conta própria.
 *
 * PRECISA limpar o Vuex, não só o localStorage. O getter `isAuthenticated` lê
 * `state.user`, que é carregado uma única vez na criação do store: apagar só o
 * localStorage deixa a memória dizendo que o usuário segue logado. O guard do
 * router então devolveria a pessoa da tela de login para o calendário, o
 * calendário dispararia requisições, elas voltariam 401, e o ciclo se repetiria
 * — laço infinito de redirecionamento. `RESET_AUTH` zera estado e storage de
 * uma vez (e ainda revoga o blob do avatar, que ficaria vazando).
 *
 * O store é resolvido em tempo de execução, dentro da função. Importar no topo
 * criaria ciclo de módulos (store → http → store); aqui o módulo já está
 * inicializado quando isto roda.
 */
function handleUnauthorized(config) {
  // SALVIA-744: a sondagem de sessão ("ainda estou logado?") recebe 401 como
  // resposta NORMAL quando não há sessão — não é expiração, não redireciona.
  if (config?._sondagem) return;

  // eslint-disable-next-line global-require
  const store = require("@/store").default;
  store.commit("auth/RESET_AUTH");

  const url = config?.url || "";
  if (ROTAS_DE_CREDENCIAL.some((rota) => url.startsWith(rota))) return;

  const hashAtual = window.location.hash || "";
  if (hashAtual.includes("/login-page")) return; // já está lá — evita laço

  // Preserva o destino para devolver a pessoa ao lugar onde ela estava,
  // no mesmo formato que o guard do router usa.
  const destino = hashAtual.replace(/^#/, "");
  const query = destino && destino !== "/"
    ? `?expirado=1&redirect=${encodeURIComponent(destino)}`
    : "?expirado=1";

  window.location.hash = `#/login-page${query}`;
}

/**
 * O backend avisa, no cabeçalho `X-Vault-Unavailable`, que o cofre de chaves
 * (OpenBao) estava fora do ar ao montar a resposta: ela veio normal, mas com
 * dados de pacientes vazios (FlagVaultUnavailable no backend).
 *
 * Um aviso só, e no máximo um por minuto: com o cofre fora, quase toda
 * requisição traria o cabeçalho, e repetir o aviso a cada uma seria ruído.
 * Sem este aviso, a pessoa veria listas com campos vazios sem saber por quê.
 */
const VAULT_NOTICE_INTERVAL_MS = 60000;
let lastVaultNoticeAt = 0;

function notifyIfVaultUnavailable(response) {
  if (response?.headers?.["x-vault-unavailable"] !== "1") return;

  const now = Date.now();
  if (now - lastVaultNoticeAt < VAULT_NOTICE_INTERVAL_MS) return;
  lastVaultNoticeAt = now;

  useSnackbar().warning(
    "O cofre de chaves está indisponível agora: alguns dados de pacientes não puderam ser abertos e aparecem vazios. Nada foi perdido — eles voltam assim que o cofre for destravado.",
    10000,
  );
}

http.interceptors.response.use(
  (res) => {
    notifyIfVaultUnavailable(res);
    // Toda mutação bem-sucedida sinaliza que dados do paciente podem ter mudado,
    // para o dashboard reconciliar todos os cards (ver patientDataBus).
    const method = (res.config?.method || "").toLowerCase();
    if (MUTATING_METHODS.includes(method)) {
      emitPatientDataChanged();
    }
    return res;
  },
  async (err) => {
    const config = err.config;
    notifyIfVaultUnavailable(err.response);

    // Retry automático em 429 (rate limit) com backoff — respeita Retry-After.
    // Auto-cura rajadas sem que o usuário veja erro nem perca dados.
    if (err.response?.status === 429 && config) {
      config._retry429 = (config._retry429 || 0) + 1;
      if (config._retry429 <= MAX_RETRIES_429) {
        const headerRetry = parseInt(err.response.headers?.["retry-after"], 10);
        const delayMs = Number.isFinite(headerRetry)
          ? headerRetry * 1000
          : Math.min(500 * 2 ** config._retry429, 8000);
        await new Promise((r) => setTimeout(r, delayMs));
        return http(config);
      }
    }

    // SALVIA-744: 419 = token CSRF ausente ou vencido (a sessão expirou e
    // levou o token junto, ou o cookie foi limpo). Renova e tenta de novo UMA
    // vez; persistindo, é sessão perdida e cai no tratamento do 401.
    if (err.response?.status === 419 && config && !config._retry419) {
      config._retry419 = true;
      await garantirCookieCsrf({ forcar: true });
      return http(config);
    }

    // Sessão expirada.
    if (err.response?.status === 401 || err.response?.status === 419) {
      handleUnauthorized(err.config);
    }

    // NOVO: se role não foi selecionada, manda pra tela de escolha
    if (
      err.response?.status === 403 &&
      err.response?.data?.code === "ROLE_REQUIRED"
    ) {
      // evite loop: se já estiver na página de escolher role, não redireciona
      if (!window.location.hash.includes("/auth/choose-role")) {
        window.location.hash = "#/auth/choose-role";
      }
    }

    // Camada 3 do tratamento de scope Google: quando o backend devolve
    // 403 scope_insufficient, dispara evento global que o GoogleReauthDialog
    // (montado no App.vue) escuta para abrir o prompt de reconexão.
    if (
      err.response?.status === 403 &&
      err.response?.data?.error === "scope_insufficient"
    ) {
      window.dispatchEvent(
        new CustomEvent("reauth-google-needed", {
          detail: {
            message: err.response.data.message,
            ownerType: err.response.data.owner_type || "user",
            reauthUrl:
              err.response.data.reauth_url || "/api/auth/google/redirect",
          },
        }),
      );
    }

    // salvia-798/800 — anexa o identificador de correlação ao erro, mas SÓ
    // quando ele ajuda o usuário.
    //
    // 4xx é falha esperada e a mensagem já orienta ("e-mail já cadastrado",
    // "sem permissão"). Mostrar um código de suporte ali vira ruído e faz a
    // pessoa reportar o que era só um campo mal preenchido.
    //
    // 5xx é o caso oposto: mensagem genérica, e sem a referência não se chega
    // à causa. Lemos do corpo e, na falta dele, do header — erro de proxy e
    // gateway chegam sem JSON.
    //
    // Falha sem resposta nenhuma (timeout, queda de rede) entra na mesma
    // trilha porque alguns proxies devolvem 504 sem corpo mas COM o header. Se
    // não vier nem isso, fica sem referência — e é o certo: a requisição pode
    // nem ter chegado ao servidor, então não há linha de log para apontar.
    const statusErro = err.response?.status;
    const inesperado = !err.response || statusErro >= 500;

    // SALVIA-951: na falta de id na resposta (proxy, WAF, timeout), usa o que
    // este cliente enviou — é o mesmo que o Laravel registrou no log, se a
    // requisição chegou até ele.
    err.requestId = inesperado
      ? err.response?.data?.request_id ||
        err.response?.headers?.["x-request-id"] ||
        err.config?.headers?.["X-Request-Id"] ||
        null
      : null;

    return Promise.reject(err);
  },
);

export default http;
